/**
 * Network Error Handler
 * Provides comprehensive error handling for network requests with retry mechanisms
 */

class NetworkErrorHandler {
  constructor() {
    this.retryAttempts = 3;
    this.retryDelay = 1000; // 1 second
    this.backoffMultiplier = 2;
  }

  /**
   * Handle network errors with retry logic
   */
  async handleRequest(requestFn, options = {}) {
    const maxRetries = options.maxRetries || this.retryAttempts;
    const baseDelay = options.retryDelay || this.retryDelay;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        const response = await requestFn();
        
        // Handle HTTP error status codes
        if (!response.ok) {
          const error = await this.handleHttpError(response, attempt, maxRetries);
          if (error) throw error;
        }
        
        return response;
      } catch (error) {
        const isLastAttempt = attempt === maxRetries;
        
        if (isLastAttempt) {
          return this.handleFinalError(error, options);
        }
        
        // Check if error is retryable
        if (!this.isRetryableError(error)) {
          throw error;
        }
        
        // Wait before retry with exponential backoff
        const delay = baseDelay * Math.pow(this.backoffMultiplier, attempt);
        await this.delay(delay);
        
        console.warn(`Retry attempt ${attempt + 1}/${maxRetries} after ${delay}ms`);
      }
    }
  }

  /**
   * Handle HTTP error responses
   */
  async handleHttpError(response, attempt, maxRetries) {
    const status = response.status;
    const isLastAttempt = attempt === maxRetries;
    
    let errorMessage;
    let errorType;
    
    switch (status) {
      case 400:
        errorType = 'VALIDATION_ERROR';
        errorMessage = 'Invalid request data. Please check your input and try again.';
        break;
      case 401:
        errorType = 'AUTHENTICATION_ERROR';
        errorMessage = 'Authentication failed. Please log in again.';
        break;
      case 403:
        errorType = 'AUTHORIZATION_ERROR';
        errorMessage = 'You do not have permission to perform this action.';
        break;
      case 404:
        errorType = 'NOT_FOUND_ERROR';
        errorMessage = 'The requested resource was not found.';
        break;
      case 429:
        errorType = 'RATE_LIMIT_ERROR';
        errorMessage = 'Too many requests. Please wait a moment and try again.';
        break;
      case 500:
        errorType = 'SERVER_ERROR';
        errorMessage = 'Server error. Please try again later.';
        break;
      case 502:
      case 503:
      case 504:
        errorType = 'SERVICE_UNAVAILABLE';
        errorMessage = 'Service temporarily unavailable. Please try again later.';
        break;
      default:
        errorType = 'UNKNOWN_ERROR';
        errorMessage = 'An unexpected error occurred. Please try again.';
    }
    
    // If not the last attempt, return null to trigger retry
    if (!isLastAttempt && this.isRetryableStatus(status)) {
      return null;
    }
    
    // Create error object
    const error = new Error(errorMessage);
    error.type = errorType;
    error.status = status;
    error.retryable = this.isRetryableStatus(status);
    
    return error;
  }

  /**
   * Handle final error after all retries
   */
  handleFinalError(error, options) {
    const errorInfo = {
      message: error.message || 'Network request failed',
      type: error.type || 'NETWORK_ERROR',
      status: error.status,
      retryable: error.retryable || false,
      timestamp: new Date().toISOString(),
      url: options.url || 'unknown'
    };
    
    // Log error for debugging
    console.error('Network error after all retries:', errorInfo);
    
    // Throw enhanced error
    const enhancedError = new Error(errorInfo.message);
    enhancedError.info = errorInfo;
    throw enhancedError;
  }

  /**
   * Check if error is retryable
   */
  isRetryableError(error) {
    // Network errors (no response)
    if (error.name === 'TypeError' && error.message.includes('fetch')) {
      return true;
    }
    
    // Timeout errors
    if (error.name === 'AbortError') {
      return true;
    }
    
    // Server errors (5xx)
    if (error.status >= 500 && error.status < 600) {
      return true;
    }
    
    // Rate limiting (429)
    if (error.status === 429) {
      return true;
    }
    
    return false;
  }

  /**
   * Check if HTTP status is retryable
   */
  isRetryableStatus(status) {
    return status >= 500 || status === 429;
  }

  /**
   * Delay utility
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get user-friendly error message
   */
  getUserFriendlyMessage(error) {
    if (error.info) {
      return error.info.message;
    }
    
    if (error.message) {
      return error.message;
    }
    
    return 'Something went wrong. Please try again.';
  }

  /**
   * Get error recovery suggestions
   */
  getRecoverySuggestions(error) {
    const suggestions = [];
    
    if (error.info?.type === 'AUTHENTICATION_ERROR') {
      suggestions.push('Try logging out and logging back in');
      suggestions.push('Check if your session has expired');
    } else if (error.info?.type === 'RATE_LIMIT_ERROR') {
      suggestions.push('Wait a few minutes before trying again');
      suggestions.push('Try refreshing the page');
    } else if (error.info?.type === 'NETWORK_ERROR') {
      suggestions.push('Check your internet connection');
      suggestions.push('Try refreshing the page');
      suggestions.push('Clear your browser cache');
    } else if (error.info?.type === 'SERVER_ERROR') {
      suggestions.push('Try again in a few minutes');
      suggestions.push('Contact support if the problem persists');
    }
    
    return suggestions;
  }
}

// Export singleton instance
export default new NetworkErrorHandler(); 